"use client"

import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Edit, Trash2, RotateCcw, UserMinus } from "lucide-react"
import { useLanguage } from "@/contexts/language-context"
import { useCodeValues } from "@/hooks/use-code-values"
import { useWorkerProfiles, type WorkerProfile } from "@/hooks/use-worker-profiles"
import type { WorkerProfileSummary } from "@/types/api/engagement"
import { ParentNewTabLink } from "@/components/ui/parent-new-tab-link"
import { type WorkerFormData as Worker, type WorkerFormLegacySyncStatus, WORKER_TYPE, WORKER_LEGACY_SYNC_STATUS } from "@/lib/schemas"
import { ENGAGEMENT_BILLING_TYPE, ENGAGEMENT_TYPE, ENGAGEMENT_STATUS } from "@/lib/constants/engagement-codes"
import { useFormatters } from "@/hooks/use-formatters"
import { payRateRows } from "@/lib/utils/worker-rate-slots"
import { parseDateOnly } from "@/lib/utils"
import type { WorkerSaveErrors } from "@/lib/utils/worker-save-errors"
import { workerCardErrors, type WorkerCardSlot, type WorkerFieldErrors } from "@/lib/utils/worker-card-errors"
import { billRateWarning } from "@/lib/utils/bill-rate-warning"
import { pendingEntries, toDateOnly } from "@/lib/utils/scheduled-budgeted-hours"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { ScheduledHoursSummary } from "./ScheduledHoursSummary"

interface WorkerCardProps {
  worker: Worker
  index: number
  hasError: boolean
  /** What the worker schema says is wrong with this worker, shown under the fields it is about. */
  fieldErrors?: WorkerFieldErrors
  /**
   * What a refused save said about this worker that still stands, by field. Carried on the card so the
   * reader can see which worker the server meant, and what to change, without opening every one of them.
   */
  saveErrors?: WorkerSaveErrors
  engagementStatus?: string
  engagementEndDate?: Date | null
  isReadonly: boolean
  billingType: string
  engagementType: string
  onEdit: (worker: Worker) => void
  onRemove: (workerUserProfileId: number, shouldSoftRemove: boolean) => void
  onRestore?: (workerUserProfileId: number) => void
  isExisting: boolean
  currency?: string
  canViewPayRates: boolean
  workerProfiles: Record<number, WorkerProfileSummary>
}

// Helper to get badge styling for worker status
const getStatusBadgeProps = (status: WorkerFormLegacySyncStatus) => {
  switch (status) {
    case "Ready":
      return {
        variant: "default" as const,
        className: "bg-green-100 text-green-800 hover:bg-green-100"
      }
    case "Error":
      return {
        variant: "destructive" as const,
        className: "bg-red-100 text-red-800 hover:bg-red-100"
      }
    case "Processing":
      return {
        variant: "secondary" as const,
        className: "bg-blue-100 text-blue-800 hover:bg-blue-100"
      }
    default:
      return {
        variant: "outline" as const,
        className: ""
      }
  }
}

/** The errors held against one card field, under it. */
const FieldErrorLines = ({ messages }: { messages?: string[] }) => (
  <>
    {messages?.map(message => (
      <p key={message} className="text-xs text-red-700 mt-1">{message}</p>
    ))}
  </>
)

export const WorkerCard = ({
  worker,
  index,
  hasError,
  fieldErrors,
  saveErrors,
  engagementStatus,
  engagementEndDate,
  isReadonly,
  billingType,
  engagementType,
  onEdit,
  onRemove,
  onRestore,
  isExisting,
  currency,
  canViewPayRates,
  workerProfiles,
}: WorkerCardProps) => {
  const { dictionary } = useLanguage()
  const { getCodeValueTextByCode, getCodeValueText } = useCodeValues()
  const { workerProfiles: workerUserProfiles } = useWorkerProfiles()
  const { formatCurrency, formatDate } = useFormatters()

  // Whether anyone is billed for hours here. Milestone billing and internal engagements carry no worker
  // Bill Rate, and a rate's billability says nothing on them.
  const billsForHours =
    billingType !== ENGAGEMENT_BILLING_TYPE.MILESTONE && engagementType !== ENGAGEMENT_TYPE.INTERNAL

  const { bySlot, byRate } = workerCardErrors(fieldErrors, saveErrors)
  const billRateWarningMessage = billRateWarning(worker, { billingType, engagementType, currency, canViewPayRates })
  const isFlagged = (slot: WorkerCardSlot) => Boolean(bySlot[slot]?.length)
  const hasSaveErrors = Object.keys(saveErrors ?? {}).length > 0

  // Which fields the card is showing. A field left off while it is blank is shown while it is flagged,
  // so its message has somewhere to sit, and an error about a field the card never shows is raised at the
  // head of the card instead, so no message is lost.
  const shownSlots: Record<Exclude<WorkerCardSlot, 'general'>, boolean> = {
    workerName: true,
    jobCategory: true,
    jobFunction: true,
    jobTitle: true,
    budgetedHours: true,
    billRate: billsForHours,
    reportingBillRate: billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE && (worker.reportingBillRate != null || isFlagged('reportingBillRate')),
    payRates: worker.type === WORKER_TYPE.REGULAR && canViewPayRates,
    paySchedule: worker.type === WORKER_TYPE.REGULAR,
    reportingPayRate: worker.type === WORKER_TYPE.T4_SALARIED && canViewPayRates,
    firstDayOfWork: Boolean(worker.firstDayOfWork) || isFlagged('firstDayOfWork'),
    lastDayOfWork: Boolean(worker.lastDayOfWork) || isFlagged('lastDayOfWork'),
  }
  const errorsUnder = (slot: Exclude<WorkerCardSlot, 'general'>) => shownSlots[slot] ? bySlot[slot] : undefined

  // A rate's error sits under its row. One about a rate with no row on the card joins the list's own.
  const rateRowCount = shownSlots.payRates ? payRateRows(worker).length : 0
  const rateErrorsUnder = (index: number) => index < rateRowCount ? byRate[index] : undefined
  const rowlessRateErrors = Object.entries(byRate)
    .filter(([index]) => Number(index) >= rateRowCount)
    .flatMap(([, messages]) => messages)
  const rateListErrors = shownSlots.payRates ? [...(bySlot.payRates ?? []), ...rowlessRateErrors] : undefined

  const headErrors = [
    ...(bySlot.general ?? []),
    ...(Object.entries(bySlot) as [WorkerCardSlot, string[]][])
      .filter(([slot]) => slot !== 'general' && !shownSlots[slot])
      .flatMap(([, messages]) => messages),
    ...(shownSlots.payRates ? [] : rowlessRateErrors),
  ]

  // Helper to determine if worker statuses should be shown
  const shouldShowWorkerStatuses = Boolean(engagementStatus) &&
    engagementStatus !== ENGAGEMENT_STATUS.DRAFT &&
    engagementStatus !== ENGAGEMENT_STATUS.PENDING_REVIEW

  // A removed worker was *terminated* (vs simply removed from the engagement) only when a termination
  // signal was saved with the removal — a termination type and/or reason. Otherwise it's just "Removed".
  const wasTerminated = Boolean(worker.isRemoved) && (
    Boolean(worker.terminationTypeId) ||
    (worker.terminationReasonIds?.length ?? 0) > 0
  )

  // "Complete" is a replacement for the green "Ready" badge: shown only once a worker has been
  // successfully processed (Ready) AND their effective last day (own date, else engagement end) is in
  // the past. Never shown alongside Processing/Error — those still need attention.
  const effectiveLastDay = worker.lastDayOfWork ?? engagementEndDate ?? null
  const isComplete = !worker.isRemoved &&
    worker.legacySyncStatus === WORKER_LEGACY_SYNC_STATUS.READY &&
    (() => {
      if (!effectiveLastDay) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return effectiveLastDay < today
    })()

  // Determine if this should be a soft remove (worker existed in previous version)
  const shouldSoftRemove = isExisting
  const removeButtonText = shouldSoftRemove
    ? (dictionary?.engagement?.core?.remove || "Remove")
    : (dictionary?.engagement?.core?.delete || "Delete")

  // Helper function to get worker display name by ID.
  // Prefer the engagement-scoped resolution from the API response — it works for restricted
  // Engagement Leads who lack row-level access to the global worker-profile list. Fall back to the
  // global list (privileged users / add-worker picker), then to a bare ID.
  const getWorkerDisplayName = (workerUserProfileId: number): string => {
    const resolved = workerProfiles[workerUserProfileId]
    if (resolved?.fullName) {
      return `${resolved.fullName} - ${workerUserProfileId}`
    }
    const profile = workerUserProfiles.find(p => p.Id === workerUserProfileId)
    return profile ? profile.DisplayValue : `Worker ${workerUserProfileId}`
  }

  // Helper function to get profile type text from code tables
  const getProfileTypeText = (profile: WorkerProfile): string => {
    if (profile.T4TypeId) {
      return getCodeValueText(profile.T4TypeId, "usr.CodeProfileWorkerT4Type") || "T4"
    } else {
      return getCodeValueText(profile.ProfileTypeId, "usr.CodeProfileType") || "Unknown"
    }
  }

  // Helper function to get profile type text for a worker. Prefer the engagement-scoped resolution
  // (code-based, against the same code tables as the global list); fall back to the global list.
  const getWorkerTypeText = (worker: Worker): string => {
    const resolved = worker.workerUserProfileId ? workerProfiles[worker.workerUserProfileId] : undefined
    if (resolved) {
      if (resolved.t4TypeCode) {
        return getCodeValueTextByCode(resolved.t4TypeCode, "usr.CodeProfileWorkerT4Type") || "T4"
      }
      if (resolved.profileTypeCode) {
        return getCodeValueTextByCode(resolved.profileTypeCode, "usr.CodeProfileType") || "Unknown"
      }
    }
    const profile = workerUserProfiles.find(p => p.Id === worker.workerUserProfileId)
    return profile ? getProfileTypeText(profile) : "Unknown"
  }

  // Helper function to format scheduled rate display text
  const getScheduledRateText = (
    scheduledRate: number | null | undefined,
    scheduledEffectiveDate: string | null | undefined,
    currentRate: number | null | undefined,
    payRateUnitCode?: string
  ): string | null => {
    // Only show scheduled rate if BOTH rate and effective date are present
    if (!scheduledRate || !scheduledEffectiveDate) {
      return null
    }

    try {
      // Use parseDateOnly to avoid UTC timezone shift
      const effectiveDate = parseDateOnly(scheduledEffectiveDate)
      if (!effectiveDate) {
        console.error('[WorkerCard] Invalid effective date format:', scheduledEffectiveDate)
        return null
      }

      // Only show scheduled rate if the effective date is in the future
      const today = new Date()
      today.setHours(0, 0, 0, 0) // Reset time to midnight for date-only comparison

      if (effectiveDate < today) {
        // Effective date has passed - don't show scheduled rate
        return null
      }

      // Don't show scheduled rate if it matches the current rate (already applied)
      if (currentRate && scheduledRate === currentRate) {
        return null
      }

      const formattedRate = formatCurrency(scheduledRate, worker.paymentCurrencyCode || currency)
      const formattedDate = formatDate(effectiveDate)
      const rateUnitLabel = getCodeValueTextByCode(payRateUnitCode || "Hour", "workorder.CodeRateUnit") || "Hour"
      return `${formattedRate} / ${rateUnitLabel} (effective: ${formattedDate})`
    } catch (error) {
      console.error('[WorkerCard] Error parsing effective date:', error)
      return null
    }
  }

  return (
    <Card className={`p-5 card-hover-effect ${
      worker.isRemoved ? "opacity-75 bg-muted/20" : ""
    } ${hasSaveErrors ? "border-destructive" : ""}`}>
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <h4 className="font-medium text-base">
            {dictionary?.engagement?.workers?.worker || "Worker"} #{index + 1}
            {(hasError || hasSaveErrors) && <span className="text-destructive"> *</span>}
          </h4>
          {/*
            A removed worker shows "Terminated" only when the removal carried a termination signal
            (type/reason) — i.e. termination was the source of the removal. A plain removal from the
            engagement shows "Removed". Legacy-sync completion alone never implies termination, so it
            is surfaced separately via its own badge below rather than relabelling this one.
          */}
          {worker.isRemoved && (
            <Badge variant="outline" className="text-xs py-0 px-1 h-5 border-red-500 text-red-700 bg-red-50">
              {wasTerminated
                ? (dictionary?.engagement?.workers?.terminated || "Terminated")
                : (dictionary?.engagement?.workers?.removed || "Removed")}
            </Badge>
          )}
          {worker.isNew && !worker.isRemoved && (
            <Badge variant="outline" className="text-xs py-0 px-1 h-5 border-blue-500 text-blue-700 bg-blue-50">
              {dictionary?.engagement?.workers?.new || "New"}
            </Badge>
          )}
          {isComplete && (
            <Badge variant="outline" className="text-xs py-0 px-1 h-5 border-slate-400 text-slate-700 bg-slate-100">
              {dictionary?.engagement?.workers?.complete || "Complete"}
            </Badge>
          )}
          {/*
            Suppress the green "Ready" sync badge when the Removed/Terminated or Complete badge already
            conveys the finished state — "Ready" would just be noise. Processing/Error stay visible since
            they signal in-flight or failed work that still needs attention.
          */}
          {shouldShowWorkerStatuses && worker.legacySyncStatus &&
            !((worker.isRemoved || isComplete) && worker.legacySyncStatus === WORKER_LEGACY_SYNC_STATUS.READY) && (
            <Badge
              {...getStatusBadgeProps(worker.legacySyncStatus)}
            >
              {worker.legacySyncStatus}
            </Badge>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
            disabled={isReadonly}
            onClick={() => onEdit(worker)}
            title={dictionary?.engagement?.core?.edit || "Edit"}
          >
            <Edit className="h-4 w-4" />
            <span className="sr-only">{dictionary?.engagement?.core?.edit || "Edit"}</span>
          </Button>
          {worker.isRemoved ? (
            // Restore button for removed workers in change requests
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
              disabled={isReadonly}
              onClick={() => onRestore?.(worker.workerUserProfileId)}
              title={dictionary?.engagement?.core?.restore || "Restore"}
            >
              <RotateCcw className="h-4 w-4" />
              <span className="sr-only">{dictionary?.engagement?.core?.restore || "Restore"}</span>
            </Button>
          ) : (
            // Remove/Delete button for active workers
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 w-8 p-0 ${shouldSoftRemove ? 'text-orange-600 hover:bg-orange-50' : 'text-destructive hover:bg-destructive/10'}`}
              disabled={isReadonly}
              onClick={() => onRemove(worker.workerUserProfileId, shouldSoftRemove)}
              title={removeButtonText}
            >
              {shouldSoftRemove ? (
                <UserMinus className="h-4 w-4" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              <span className="sr-only">
                {removeButtonText}
              </span>
            </Button>
          )}
        </div>
      </div>
      {/* Explain a failed sync inline — otherwise the Error badge alone gives the user nothing to act on. */}
      {worker.legacySyncStatus === WORKER_LEGACY_SYNC_STATUS.ERROR && worker.legacySyncError && (
        <p className="text-xs text-red-700 -mt-2 mb-3">{worker.legacySyncError}</p>
      )}
      {/* Whatever is wrong with this worker that no field on the card can carry. Everything else sits
          under the field it is about. */}
      {headErrors.map(message => (
        <p key={message} className="text-xs text-red-700 -mt-2 mb-3">{message}</p>
      ))}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-muted-foreground mb-1">
            {dictionary?.engagement?.workers?.workerName || "Worker Name"}
          </p>
          {/* The profile screen lives in the Angular shell, and the route takes the userProfileId
              alone: the shell resolves contact id and profile type, which a restricted Engagement
              Lead cannot look up here (see getWorkerDisplayName). */}
          <p className="font-medium">
            {worker.workerUserProfileId > 0 ? (
              <ParentNewTabLink
                route={`/next/contact/userprofile/${worker.workerUserProfileId}`}
                className="font-medium text-blue-600 hover:text-blue-800 hover:underline underline-offset-4"
              >
                {getWorkerDisplayName(worker.workerUserProfileId)}
              </ParentNewTabLink>
            ) : (
              getWorkerDisplayName(worker.workerUserProfileId)
            )}
          </p>
          <FieldErrorLines messages={errorsUnder('workerName')} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">
            {dictionary?.engagement?.workers?.type || "Type"}
          </p>
          <Badge variant={worker.type === WORKER_TYPE.T4_SALARIED ? "secondary" : "outline"} className="mt-1">
            {getWorkerTypeText(worker)}
          </Badge>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">
            {dictionary?.engagement?.workers?.jobCategory || "Job Category"}
          </p>
          <p className="font-medium">
            {getCodeValueTextByCode(worker.jobCategoryCode || "", "workorder.CodeJobCategory") || "-"}
          </p>
          <FieldErrorLines messages={errorsUnder('jobCategory')} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">
            {dictionary?.engagement?.workers?.jobFunction || "Job Function"}
          </p>
          <p className="font-medium">
            {getCodeValueTextByCode(worker.jobFunctionCode || "", "workorder.CodeJobFunction") || "-"}
          </p>
          <FieldErrorLines messages={errorsUnder('jobFunction')} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">
            {dictionary?.engagement?.workers?.jobTitle || "Job Title"}
          </p>
          <p className="font-medium">
            {worker.jobTitle || "-"}
          </p>
          <FieldErrorLines messages={errorsUnder('jobTitle')} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">
            {dictionary?.engagement?.workers?.lineOfBusiness || "Line of Business"}
          </p>
          <p className="font-medium">
            {getCodeValueTextByCode(worker.lineOfBusinessCode || "", "org.CodeLineOfBusiness") || worker.lineOfBusinessCode || "-"}
          </p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">
            {dictionary?.engagement?.workers?.budgetedHoursPerDay || "Budgeted Hours per Day"}
          </p>
          <p className="font-medium">{worker.budgetedHours}</p>
          {/* Pending changes only: applied and cancelled ones are in the engagement history, and a removed
              worker's are cancelled by the save. */}
          {!worker.isRemoved && pendingEntries(worker.scheduledBudgetedHours).length > 0 && (
            <HoverCard openDelay={150} closeDelay={100}>
              <HoverCardTrigger asChild>
                <button
                  type="button"
                  className="mt-0.5 block text-xs text-muted-foreground underline decoration-dotted underline-offset-2 cursor-default"
                >
                  {dictionary?.engagement?.workers?.seeScheduledChanges || "See scheduled changes"}
                </button>
              </HoverCardTrigger>
              <HoverCardContent align="start" className="w-auto max-w-sm px-3 py-2">
                <ScheduledHoursSummary
                  entries={worker.scheduledBudgetedHours}
                  lastDay={effectiveLastDay ? toDateOnly(effectiveLastDay) : null}
                />
              </HoverCardContent>
            </HoverCard>
          )}
          <FieldErrorLines messages={errorsUnder('budgetedHours')} />
        </div>
        {shownSlots.billRate && (
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {dictionary?.engagement?.workers?.billRate || "Bill Rate"}
            </p>
            <p className="font-medium">
              {worker.billRate ? `${formatCurrency(worker.billRate, currency)} ${getCodeValueTextByCode(worker.billRateUnit || "", "workorder.CodeRateUnit") || ""}` : "-"}
            </p>
            <FieldErrorLines messages={errorsUnder('billRate')} />
            {billRateWarningMessage && (
              <p className="text-xs text-amber-700 mt-1">{billRateWarningMessage}</p>
            )}
          </div>
        )}
        {shownSlots.reportingBillRate && (
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {dictionary?.engagement?.workers?.reportingBillRate || "Reporting Bill Rate"}
            </p>
            <p className="font-medium">
              {worker.reportingBillRate != null ? formatCurrency(worker.reportingBillRate, currency) : "-"}
            </p>
            <FieldErrorLines messages={errorsUnder('reportingBillRate')} />
          </div>
        )}

        {worker.type === WORKER_TYPE.REGULAR && (
          <>
            {/* Pay Rate (incl. scheduled line) is gated by canViewPayRates; Pay Schedule below is NOT. */}
            {canViewPayRates && (() => {
              // Every rate is labelled, one or many: a bare amount says nothing about which rate it is,
              // and a worker on a single rate still has a type and often a description worth reading. The
              // bare amount is left for a worker carrying no rate list at all, where there is nothing to
              // label them with.
              const rates = payRateRows(worker)

              const isLabelledList = rates.length > 0
              const labelOf = (rate: typeof rates[number]) => {
                const rateTypeLabel =
                  getCodeValueTextByCode(rate.rateTypeCode || "", "workorder.CodeRateType") || rate.rateTypeCode || "-"
                const description = rate.description?.trim()

                return description ? `${rateTypeLabel} (${description})` : rateTypeLabel
              }
              const unitOf = (rate: typeof rates[number]) =>
                getCodeValueTextByCode(rate.payRateUnitCode || worker.payRateUnit || "", "workorder.CodeRateUnit")

              // Whether hours at this rate are invoiced, worded as the worker dialog words it. Left out
              // where nothing on this engagement is billed, the same way the Bill Rate field is, since
              // there the answer is the same for every rate and says nothing.
              const billabilityOf = (rate: typeof rates[number]) =>
                rate.billable === false
                  ? (dictionary?.engagement?.workers?.nonBillable || "Non-billable")
                  : (dictionary?.engagement?.workers?.billable || "Billable")
              const scheduledOf = (rate: typeof rates[number]) =>
                getScheduledRateText(rate.scheduledPayRate, rate.scheduledPayRateEffectiveDate, rate.payRate, rate.payRateUnitCode || worker.payRateUnit || undefined)

              // Pay Rates stays one field in its own column, its value a stack of one rate per line, so
              // the card's two-column rhythm is untouched and nothing can misalign. The amount leads
              // because that is what a reader scans for; the label sits beside it in normal weight.
              if (isLabelledList) {
                return (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {rates.length > 1
                        ? (dictionary?.engagement?.workers?.payRates || "Pay Rates")
                        : (dictionary?.engagement?.workers?.payRate || "Pay Rate")}
                    </p>
                    <div className="flex flex-col gap-2 mt-0.5">
                      {rates.map((rate, index) => (
                        <div key={rate.rateTypeCode || index}>
                          {/* Label then amount, in the order and the widths the worker profile puts them
                              in, so one rate reads the same on either screen. */}
                          {/* Both columns are a fixed width and sit next to each other, so the amounts line
                              up down the list without the label stretching and pushing them to the far edge.
                              Any slack falls after the amount rather than between the two. */}
                          <div className="flex items-baseline gap-8">
                            <span className="basis-[180px] min-w-0 truncate" title={labelOf(rate)}>
                              {labelOf(rate)}
                            </span>
                            <span className="shrink-0 font-medium tabular-nums whitespace-nowrap">
                              {rate.payRate ? formatCurrency(rate.payRate, worker.paymentCurrencyCode || currency) : "-"}
                              {/* Coerced, because a zero amount would otherwise render as the number
                                  itself and read as "-0" after the dash above. */}
                              {!!rate.payRate && unitOf(rate) && (
                                <span className="ml-1 text-xs font-normal text-muted-foreground">{unitOf(rate)}</span>
                              )}
                            </span>
                            {billsForHours && (
                              <span className="shrink-0 text-xs text-muted-foreground whitespace-nowrap">
                                {billabilityOf(rate)}
                              </span>
                            )}
                          </div>
                          {/* Worded exactly as the worker profile words it, so the same pending change
                              reads the same on both screens. */}
                          {scheduledOf(rate) && (
                            <p className="text-xs text-muted-foreground italic mt-0.5">
                              <span className="font-normal">Scheduled: </span>
                              {scheduledOf(rate)}
                            </p>
                          )}
                          <FieldErrorLines messages={rateErrorsUnder(index)} />
                        </div>
                      ))}
                    </div>
                    <FieldErrorLines messages={rateListErrors} />
                  </div>
                )
              }

              // A worker carrying no rate list at all, which is a salaried one or a placement nothing has
              // written rates onto yet. There is nothing to label, so the field says so and stops.
              return (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">
                    {dictionary?.engagement?.workers?.payRate || "Pay Rate"}
                  </p>
                  <p className="font-medium">-</p>
                  <FieldErrorLines messages={rateListErrors} />
                </div>
              )
            })()}
            {/* Pay Schedule — always visible regardless of pay-rate visibility */}
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {dictionary?.engagement?.workers?.paySchedule || "Pay Schedule"}
              </p>
              <p className="font-medium">
                {getCodeValueTextByCode(worker.paySchedule || "", "workorder.CodePaymentReleaseSchedule") || "-"}
              </p>
              <FieldErrorLines messages={errorsUnder('paySchedule')} />
            </div>
          </>
        )}

        {canViewPayRates && worker.type === WORKER_TYPE.T4_SALARIED && (() => {
          const scheduledRateText = getScheduledRateText(worker.scheduledReportingPayRate, worker.scheduledPayRateEffectiveDate, worker.reportingPayRate, "Hour")
          return (
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                {dictionary?.engagement?.workers?.reportingPayRate || "Reporting Pay Rate"}
              </p>
              <p className="font-medium">
                {formatCurrency(worker.reportingPayRate, worker.paymentCurrencyCode || currency)}
              </p>
              {scheduledRateText && (
                <p className="text-xs text-muted-foreground italic mt-1">
                  <span className="font-normal">Scheduled: </span>
                  {scheduledRateText}
                </p>
              )}
              <FieldErrorLines messages={errorsUnder('reportingPayRate')} />
            </div>
          )
        })()}

        {shownSlots.firstDayOfWork && (
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {dictionary?.engagement?.workers?.engagementStartDate || "Engagement Start Date"}
            </p>
            <p className="font-medium">{worker.firstDayOfWork ? formatDate(worker.firstDayOfWork) : "-"}</p>
            <FieldErrorLines messages={errorsUnder('firstDayOfWork')} />
          </div>
        )}

        {shownSlots.lastDayOfWork && (
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {dictionary?.engagement?.workers?.lastDayOfWork || "Last Day of Work"}
            </p>
            <p className="font-medium">
              {worker.lastDayOfWork ? formatDate(worker.lastDayOfWork) : "-"}
            </p>
            <FieldErrorLines messages={errorsUnder('lastDayOfWork')} />
          </div>
        )}
      </div>
    </Card>
  )
}

export default WorkerCard 